import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useForm } from "react-hook-form";
import { AxiosService } from "../../services/axios/axiosService";
import CancelIcon from "@mui/icons-material/Cancel";
const MAX_FILE_MB = 10;

//Method to check if the file is allowed to be uploaded
const isAllowedResumeType = (file) => {
  const name = (file?.name || "").toLowerCase();
  return name.endsWith(".pdf") || name.endsWith(".doc") || name.endsWith(".docx");
};

//Method to convert bytes to megabytes
const bytesToMb = (b) => (typeof b === "number" ? b / (1024 * 1024) : 0);

//Method to safely convert a value to a string
const safeStr = (v) => (typeof v === "string" ? v : v == null ? "" : String(v));

// Method to format phone input (lightweight masking; no extra libs)
const formatPhoneForDisplay = (input) => {
  const raw = safeStr(input);
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (!digits) return "";
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/** Map `/api/Position` row to dropdown option (show title; keep id + title for submit). */
const toJobOption = (item) => {
  const id = item?.id;
  const title = safeStr(item?.title).trim();
  if (id == null || !title) return null;
  const jobOpeningID = item?.jobOpeningID != null ? safeStr(item.jobOpeningID).trim() : "";
  return {
    id: String(id),
    label: title,
    title,
    jobOpeningID,
  };
};

//Component to add a candidate
const AddCandidateDialog = ({ open, onClose, onSaved, existingEmails = [], jobPosts = [] }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [fetchedJobPosts, setFetchedJobPosts] = useState([]);
  const [isJobPostsLoading, setIsJobPostsLoading] = useState(false);
  const jobOptions = useMemo(() => {
    if (Array.isArray(fetchedJobPosts) && fetchedJobPosts.length > 0) return fetchedJobPosts;
    return Array.isArray(jobPosts) ? jobPosts : [];
  }, [fetchedJobPosts, jobPosts]);
  const jobPostLabelById = useMemo(() => {
    const map = new Map();
    for (const j of jobOptions) {
      const id = j?.id ?? j?.value ?? j;
      const label = j?.label ?? j?.title ?? j?.name ?? String(j);
      if (id != null) map.set(String(id), label);
    }
    return map;
  }, [jobOptions]);

  const jobOptionByPostId = useMemo(() => {
    const map = new Map();
    for (const j of jobOptions) {
      const key = j?.id ?? j?.value;
      if (key != null) map.set(String(key), j);
    }
    return map;
  }, [jobOptions]);
  const existingEmailSet = useMemo(() => {
    const list = Array.isArray(existingEmails) ? existingEmails : [];
    return new Set(list.map((e) => safeStr(e).trim().toLowerCase()).filter(Boolean));
  }, [existingEmails]);

  const [resumeFile, setResumeFile] = useState(null);

  const {    
    handleSubmit,
    reset,
    setError,
    clearErrors,
    register,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      fullName: "",
      email: "",
      contactNumber: "",
      location: "",
      experience: "",
      jobPostId: "",
      resume: null,
      jobId: "",
      jobTitle: "",
    },
  });

  //Method to handle the close of the dialog
  const handleClose = () => {
    reset();
    onClose?.();
    setResumeFile(null);
    clearErrors();
  };

  //Method to handle the dialog close
  const handleDialogClose = (_event, reason) => {
    // Prevent closing on backdrop click / ESC. Close only via explicit actions.
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    handleClose();
  };

  //to get the value of the job post
  const jobPostValue = watch("jobPostId");
  const emailValue = watch("email");

  React.useEffect(() => {
    const email = safeStr(emailValue).trim().toLowerCase();
    if (!email) {
      if (errors.email?.type === "validate" && errors.email?.message === "Email already exists.") {
        clearErrors("email");
      }
      return;
    }

    const isFormatOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!isFormatOk) return;

    if (existingEmailSet.has(email)) {
      setError("email", { type: "validate", message: "Email already exists." });
      return;
    }

    if (errors.email?.type === "validate" && errors.email?.message === "Email already exists.") {
      clearErrors("email");
    }
  }, [emailValue, existingEmailSet, setError, clearErrors, errors.email]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const loadPositions = async () => {
      setIsJobPostsLoading(true);
      try {
        const res = await AxiosService.get("/api/Position");
        const list = Array.isArray(res) ? res : res?.data ?? [];
        const byId = new Map();
        for (const row of Array.isArray(list) ? list : []) {
          const opt = toJobOption(row);
          if (opt) byId.set(opt.id, opt);
        }
        if (!cancelled) setFetchedJobPosts([...byId.values()]);
      } catch {
        if (!cancelled) setFetchedJobPosts([]);
      } finally {
        if (!cancelled) setIsJobPostsLoading(false);
      }
    };

    loadPositions();
    return () => {
      cancelled = true;
    };
  }, [open]);

  //Method to pick a file
  const onPickFile = (file) => {
    if (!file) return;
    if (!isAllowedResumeType(file)) {
      setValue("resume", null, { shouldValidate: true, shouldDirty: true });
      setError("resume", { type: "validate", message: "Only PDF, DOC, DOCX files are allowed." });
      return;
    }
    if (bytesToMb(file.size) > MAX_FILE_MB) {
      setValue("resume", null, { shouldValidate: true, shouldDirty: true });
      setError("resume", { type: "validate", message: `Max file size is ${MAX_FILE_MB}MB.` });
      return;
    }
    setResumeFile(file);
    setValue("resume", file, { shouldValidate: true, shouldDirty: true });
    clearErrors("resume");
  };

  //Method to remove the resume
  const handleRemoveResume = (e) => {
    e?.stopPropagation?.();
    setResumeFile(null);
    setValue("resume", null, { shouldValidate: true, shouldDirty: true });
    // Keep UX clean: removing a file shouldn't immediately show an error.
    clearErrors("resume");
    const inputEl = document.getElementById("resume-upload-input");
    if (inputEl) inputEl.value = "";
  };

  //Method to handle the valid submit
  const submitCandidateForm = async (data) => {
    try {
      clearErrors("root");
      const postId = safeStr(data?.jobPostId).trim();
      const selected = postId ? jobOptionByPostId.get(postId) : null;
      const positionId = safeStr(selected?.id ?? data?.jobId).trim();
      const positionTitle = safeStr(selected?.title ?? selected?.label ?? data?.jobTitle).trim();
      const openingId = safeStr(selected?.jobOpeningID).trim() || positionId;

      const formData = new FormData();
      formData.append("Candidate_Name", safeStr(data?.fullName).trim());
      formData.append("Email", safeStr(data?.email).trim());
      formData.append("Candidate_ID", '');
      formData.append("JobOpeningId", openingId);
      formData.append("Application_ID", '');
      formData.append("Contact_Number", safeStr(data?.contactNumber).trim());
      formData.append("Location", safeStr(data?.location).trim());
      formData.append("jobId", positionId);
      formData.append("jobTitle", positionTitle);
      formData.append("OverallExperience", safeStr(data?.experience).trim());
      if (resumeFile) formData.append("Resume", resumeFile);
      formData.append("AutoScreening", "false");

      await AxiosService.post("/api/candidate", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      await onSaved?.();
      handleClose();
    } catch (e) {
      setError("root", {
        type: "server",
        message: safeStr(e?.message) || "Unable to save candidate. Please try again.",
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      fullScreen={isSmallScreen}
      maxWidth="sm"
      disableEscapeKeyDown
      disableBackdropClick
      PaperProps={{ sx: { borderRadius: isSmallScreen ? 0 : 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>Add Candidate</DialogTitle>
      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={1.5}>
          {!!errors.root?.message && (
            <Typography
              variant="body2"
              color="error"
              fontWeight={700}
              sx={{
                border: `1px solid ${theme.palette.error.light}`,
                bgcolor: "rgba(239, 68, 68, 0.06)",
                borderRadius: 2,
                px: 1.5,
                py: 1,
              }}
            >
              {errors.root.message}
            </Typography>
          )}

          <TextField
            label="Full Name *"
            placeholder="e.g., John Smith"
            error={!!errors.fullName}
            helperText={errors.fullName?.message || " "}
            size="small"
            fullWidth
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            {...register("fullName", {
              required: "Full name is required.",
              validate: (v) => (safeStr(v).trim() ? true : "Full name is required."),
            })}
          />

          <TextField
            label="Email Address *"
            placeholder="john.smith@email.com"
            error={!!errors.email}
            helperText={errors.email?.message || " "}
            size="small"
            fullWidth
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            {...register("email", {
              required: "Email is required.",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Enter a valid email address.",
              },
            })}
          />

          <TextField
            label="Contact Number *"
            placeholder="123-456-7890"
            error={!!errors.contactNumber}
            helperText={errors.contactNumber?.message || " "}
            size="small"
            fullWidth
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            inputProps={{ inputMode: "tel" }}
            {...register("contactNumber", {
              required: "Contact number is required.",
              onChange: (e) => {
                const formatted = formatPhoneForDisplay(e?.target?.value);
                if (e?.target) e.target.value = formatted;
                setValue("contactNumber", formatted, { shouldDirty: true, shouldValidate: true });
                clearErrors("contactNumber");
              },
              validate: (v) => {
                const raw = safeStr(v).trim();
                if (!raw) return "Contact number is required.";
                const digits = raw.replace(/\D/g, "");
                if (digits.length !== 10) return "Enter a valid 10-digit phone number.";
                // Avoid obviously invalid numbers like all same digits.
                if (/^(\d)\1+$/.test(digits)) {
                  return "Enter a valid phone number.";
                }
                return true;
              },
            })}
          />
     
          <TextField
            label="Overall Experience(in years) *"
            placeholder="e.g., 5 years"
            error={!!errors.experience}
            helperText={errors.experience?.message || " "}
            size="small"
            fullWidth
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            {...register("experience", {
              required: "Overall experience is required.",
              validate: (v) => (safeStr(v).trim() ? true : "Overall experience is required."),
            })}
          />
            <TextField
            label="Location"
            placeholder="e.g., San Francisco, CA"
            size="small"
            fullWidth
            helperText={" "}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            {...register("location")}
          />
          <FormControl size="small" fullWidth error={!!errors.jobPostId}>
            <InputLabel id="job-post-label" shrink>
              Job Post Applied *
            </InputLabel>
            <Select
              labelId="job-post-label"
              label="Job Post Applied *"
              value={jobPostValue ?? ""}
              disabled={isJobPostsLoading}
              
              onChange={(e) => {
                const value = safeStr(e.target.value).trim();
                setValue("jobPostId", value, { shouldValidate: true, shouldDirty: true });
                const opt = value ? jobOptionByPostId.get(value) : null;
                setValue("jobId", opt ? safeStr(opt.id).trim() : "", { shouldDirty: true });
                setValue("jobTitle", opt ? safeStr(opt.title ?? opt.label).trim() : "", { shouldDirty: true });
                clearErrors("jobPostId");
              }}
              displayEmpty
              renderValue={(v) => {
                if (!v) return "Select a job post";
                return jobPostLabelById.get(String(v)) ?? String(v);
              }}
              sx={{ borderRadius: 2, fontSize: "14px" }}
              inputProps={{
                ...register("jobPostId", { required: "Job post is required." }),
              }}
            >
              <MenuItem value=""sx={{fontSize: "14px"}} disabled>
                <em>{isJobPostsLoading ? "Loading positions..." : "Select a job post"}</em>
              </MenuItem>
              {jobOptions.map((j) => (
                <MenuItem
                  size="small"
                  sx={{ fontSize: "14px" }}
                  key={j.id ?? j.value ?? j}
                  value={j.id ?? j.value ?? j}
                >
                  {j.title ?? j.label ?? j.name ?? String(j)}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="error" sx={{ minHeight: 18, mt: 0.5 }}>
              {errors.jobPostId?.message || " "}
            </Typography>
          </FormControl>

          <Box>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.75 }}>
              Upload Resume *
            </Typography>

            <input
              type="hidden"
              {...register("resume", { required: "Please upload a file" })}
            />

            <Box
              role="button"
              tabIndex={0}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer?.files?.[0];
                onPickFile(file);
              }}
              sx={{
                border: `1.5px dashed ${theme.palette.divider}`,
                borderRadius: 2.5,
                p: 2.25,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: "rgba(148,163,184,0.04)",
                transition: "transform 120ms ease, background-color 120ms ease",
                "&:hover": { bgcolor: "rgba(148,163,184,0.07)", transform: "translateY(-1px)" },
              }}
              
            >
            
             <Button
              onClick={() => {
                const el = document.getElementById("resume-upload-input");
                el?.click?.();
              }}
              variant="outlined"
              size="small"
              color="primary"
              startIcon={<CloudUploadOutlinedIcon />}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Upload
            </Button>
             
             <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Supports PDF, DOC, DOCX (Max {MAX_FILE_MB}MB) files
              </Typography>
             {/* <Typography variant="caption" color="text.secondary">
                PDF, DOC, DOCX (Max {MAX_FILE_MB}MB)
              </Typography> */}
              {resumeFile && (
                <Box
                  sx={{
                    mt: 1.25,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.75,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{
                      fontWeight: 400,
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={resumeFile.name}
                  >
                    {resumeFile.name}
                  </Typography>
                  <CancelIcon fontSize="small" color="error"  onClick={handleRemoveResume} />
                 
                </Box>
              )}

              <input
                id="resume-upload-input"
                type="file"
                accept=".pdf,.doc,.docx"
                style={{ display: "none" }}
                onChange={(e) => onPickFile(e.target.files?.[0])}
              />
            </Box>
            <Typography
              variant="caption"
              color={errors.resume ? "error" : "text.secondary"}
              sx={{ display: "block", mt: 0.75 }}
            >
              {errors.resume?.message || " "}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          flexDirection: { xs: "column-reverse", sm: "row" },
          alignItems: "stretch",
          gap: 1,
          "& > :not(:first-of-type)": { ml: { xs: 0, sm: 1 } },
        }}
      >
        <Button
          onClick={handleClose}
          color="secondary"
          variant="outlined"
          fullWidth
          sx={{ textTransform: "none", borderRadius: 2, width: { xs: "100%", sm: "auto" } }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(submitCandidateForm)}
          disabled={isSubmitting}
          variant="contained"
          fullWidth
          sx={{ textTransform: "none", borderRadius: 2, width: { xs: "100%", sm: "auto" } }}
        >
          Save Candidate
          {isSubmitting && <CircularProgress size={18} color="inherit" sx={{ ml: 1 }} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCandidateDialog;

